import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';
import { useAccessControl } from '../../hooks/useAccessControl';
import { ACTIONS } from '../../hooks/usePermissions';
import { useAppSnackbar } from '../common/TableDesign';
import LedgerListPage from './LedgerListPage';
import LedgerFormDialog from './LedgerFormDialog';

const getErrorMessage = (error, fallback = 'Operation failed') =>
  error?.response?.data?.message || fallback;

/**
 * Self-contained CRUD section for a ledger configuration entity.
 *
 * descriptor: {
 *   resource, title, subtitle?, addLabel?, idKey,
 *   searchPlaceholder?, emptyMessage?,
 *   columns: [{ label, render?, key?, align? }],
 *   fields: [...LedgerFormDialog fields],
 *   initialValues: {...},
 *   fetchList: (params) => Promise,
 *   createItem: (payload) => Promise,
 *   updateItem: (id, payload) => Promise,
 *   deleteItem: (id) => Promise,
 *   deleteMessage?: (item) => string,
 *   filters?: [...LedgerListPage filters],
 *   mapToForm?: (item) => values,
 *   mapToPayload?: (values, isEdit) => payload,
 *   validate?: (values) => string|null (error message),
 * }
 */
const ConfigSection = ({ descriptor }) => {
  const { can } = useAccessControl();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [values, setValues] = useState(descriptor.initialValues || {});
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const emptyMessage =
    descriptor.emptyMessage ||
    (descriptor.parentFilter?.required
      ? `Select a ${descriptor.parentFilter.label} to view records.`
      : undefined);

  const {
    resource,
    title,
    subtitle,
    addLabel,
    idKey,
    searchPlaceholder,
    columns,
    fields,
    filters,
    fetchList,
    createItem,
    updateItem,
    deleteItem,
    deleteMessage,
    mapToForm,
    mapToPayload,
    validate,
    parentFilter,
  } = descriptor;
  const [parentOptions, setParentOptions] = useState([]);
  const [parentId, setParentId] = useState('');

  useEffect(() => {
    if (!parentFilter) return;
    parentFilter
      .fetchOptions()
      .then((options) => setParentOptions(options || []))
      .catch(() => setParentOptions([]));
  }, [parentFilter]);

  const openAdd = () => {
    setEditing(null);
    setValues(descriptor.initialValues || {});
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setValues(mapToForm ? mapToForm(item) : { ...(descriptor.initialValues || {}), ...item });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (parentFilter?.required && !parentId) {
      showSnackbar(`Select a ${parentFilter.label} first`, 'error');
      return;
    }
    if (validate) {
      const error = validate(values);
      if (error) {
        showSnackbar(error, 'error');
        return;
      }
    }
    let payload = mapToPayload ? mapToPayload(values, !!editing) : { ...values };
    if (parentFilter && parentId) {
      payload = { ...payload, [parentFilter.key]: parentId };
    }
    try {
      setSaving(true);
      if (editing) {
        const id = editing[idKey] ?? editing.id;
        await updateItem(id, payload);
        showSnackbar(`${title.slice(0, -1) || 'Record'} updated successfully`, 'success');
      } else {
        await createItem(payload);
        showSnackbar(`${title.slice(0, -1) || 'Record'} created successfully`, 'success');
      }
      setShowForm(false);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  // Parent-scoped lists (e.g. rates of a tax type): filter the query and
  // hide the parent field in the form (it is injected on submit).
  const listParams = (params) =>
    parentFilter && parentId ? { ...params, [parentFilter.key]: parentId } : params;
  const visibleFields = parentFilter
    ? fields.filter((f) => f.key !== parentFilter.key)
    : fields;

  return (
    <>
      {parentFilter && (
        <Box sx={{ mb: 2, maxWidth: 420 }}>
          <Autocomplete
            fullWidth
            size="small"
            options={parentOptions}
            getOptionLabel={(o) => o?.label ?? String(o?.value ?? '')}
            value={parentOptions.find((o) => o.value === parentId) || null}
            onChange={(_, option) => setParentId(option ? option.value : '')}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            renderInput={(params) => (
              <TextField {...params} label={parentFilter.label} required={!!parentFilter.required} />
            )}
          />
        </Box>
      )}
      <LedgerListPage
        resource={resource}
        title={title}
        subtitle={subtitle}
        addLabel={addLabel || `Add ${title.slice(0, -1) || 'Record'}`}
        idKey={idKey}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        refreshKey={refreshKey}
        fetchItems={(params) => fetchList(listParams(params))}
        onAdd={can(resource, ACTIONS.CREATE) && (!parentFilter?.required || parentId) ? openAdd : null}
        onEdit={can(resource, ACTIONS.UPDATE) ? openEdit : null}
        onDelete={
          can(resource, ACTIONS.DELETE)
            ? async (item) => {
                await deleteItem(item[idKey] ?? item.id);
              }
            : null
        }
        deleteMessage={deleteMessage}
        filters={filters}
        columns={columns}
      />
      <LedgerFormDialog
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit ${title.slice(0, -1) || 'Record'}` : `Add ${title.slice(0, -1) || 'Record'}`}
        fields={visibleFields}
        values={values}
        onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
        onSubmit={handleSubmit}
        saving={saving}
      />
      {SnackbarComponent}
    </>
  );
};

export default ConfigSection;
